import type {ReactElement} from 'react';
import React from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelFooter, PanelHeader} from '@Components/panel';
import {useUpdateEffect} from 'ahooks';
import {SlideshowListOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-list-option';
import {DottedIconButton} from '@Components/dotted-icon-button';
import {ContextualMenu} from '@Components/contextual-menu';
import {SlideshowIntroDelayOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/slideshow-intro-delay-option';
import './poster-editor-slideshow-slides-panel.scss';
import {openPosterEditorAddSlideModal} from '@Modals/poster-editor-add-slide-modal';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetSelectedSlideId} from '@Hooks/poster-editor/useGetSelectedSlideId';
import {useGetKeyfromSlideshowItem} from '@Hooks/poster-editor/useGetKeyfromSlideshowItem';

interface PosterEditorSlideshowSlidesPanelProps {
  panelId: string;
}

export default function PosterEditorSlideshowSlidesPanel({...props}: PosterEditorSlideshowSlidesPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const activeSlideUID = useGetSelectedSlideId();
  const activeSlide = activeSlideUID && activeItem?.isSlideshow() ? activeItem.getSelectedSlide() : null;
  const onPageStartTime = useGetKeyfromSlideshowItem('onPageStartTime') ?? 0;

  useUpdateEffect((): void => {
    onClose();
  }, [activeItemId]);

  if (!activeItem || !activeItem.isSlideshow() || !activeSlide) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_slides')} />;
  };

  const onChangeIntroDelay = (updatedValue: number): void => {
    activeItem.updateOnPageStartTime(updatedValue, false);
  };

  const onChangeIntroDelayEnded = (updatedValue: number): void => {
    const getAllowedIntroDelayValue = activeItem.getMaxPossibleIntroDelayFromValue(updatedValue);
    activeItem.updateOnPageStartTime(getAllowedIntroDelayValue);
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu id="slideshow-content " contentClassName="spacing-p-t-1 spacing-p-b-4 spacing-p-r-4 spacing-p-l-4">
        <div>
          <SlideshowIntroDelayOption
            introDelay={onPageStartTime}
            onChangeIntroDelay={(val: number): void => {
              onChangeIntroDelay(val);
            }}
            onChangeIntroDelayEnded={(val: number): void => {
              onChangeIntroDelayEnded(val);
            }}
          />
          <SlideshowListOption />
        </div>
      </ContextualMenu>
    );
  };

  const onClickAddSlide = (): void => {
    openPosterEditorAddSlideModal();
  };

  const getFooter = (): ReactElement => {
    return (
      <PanelFooter
        className="_full-width"
        fullWidthPrimaryActions
        border
        primaryActions={[
          <DottedIconButton
            onClick={onClickAddSlide}
            key="add-slideshow-icon"
            btnIcon="icon-plus"
            className="add-slideshow-dotted-button -horizontal add-slide-dotted-icon-btn"
            btnText=""
          />,
        ]}
      />
    );
  };

  return <Panel panelHeader={getHeader()} panelFooter={getFooter()} panelContent={getContent()} />;
}
